import { Box } from '@mui/joy';
import { FilterModel } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AgGridTable, SlidingPanelContent } from 'core/components';
import { TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider, SelectionCountProvider } from 'core/states';
import { useGetSalesHistory } from 'parts/hooks';
import { IReusePriceConfig, ISalesHistoryRequestPayload } from 'parts/types';

import { columnDef } from './columns';
import { PartDetailsTab } from '../enums';
import {
  hiddenNoRowsOverlaySx,
  useLoadErrorNotification,
  usePartDetailsScrollToFocus,
} from '../hooks';

const MIN_ROWS_FOR_FOCUS_TRIGGER = 3;

interface IProps {
  requestPayload: ISalesHistoryRequestPayload;
  initialState?: {
    filter: {
      filterModel: FilterModel;
    };
  };
  reusePrice?: IReusePriceConfig;
  fullWidth?: boolean;
  enabled: boolean;
}

export const SalesHistory = ({
  requestPayload,
  initialState,
  reusePrice,
  fullWidth = true,
  enabled,
}: IProps) => {
  const { t } = useTranslation();

  const queryEnabled = enabled && !!requestPayload?.filters?.partNumber;

  const { records, isLoading, isFetching, isError, loadingPercentage, currentPage } =
    useGetSalesHistory(requestPayload, { enabled: queryEnabled, fetchAllPages: true });

  const isLoadFailed = queryEnabled && isError && !isFetching;

  useLoadErrorNotification({
    isLoadFailed,
    title: t('errorTitleSalesHistoryLoad'),
    message: t('messageSalesHistoryLoadError'),
  });

  const { panelScrollAnchorRef, onBodyScroll } = usePartDetailsScrollToFocus(
    PartDetailsTab.HISTORY,
    {
      getRowCount: () => records?.length ?? 0,
      minRowsForTrigger: MIN_ROWS_FOR_FOCUS_TRIGGER,
    }
  );

  const columnDefs = useMemo(() => columnDef({ reusePrice }), [reusePrice]);

  // A failed first page leaves currentPage undefined, which keeps the grid's loading overlay on.
  const isQueryDataShown = queryEnabled && !isLoadFailed;

  return (
    <Box ref={panelScrollAnchorRef} sx={{ display: 'contents' }}>
      <SelectionCountProvider>
        <AgGridClientTableProvider
          tableId="salesHistoryTable"
          entityType="OrderPart"
          columnDefs={columnDefs}
          data={isQueryDataShown ? records : []}
          isDataLoading={isQueryDataShown && isLoading}
          loadingPercentage={isQueryDataShown ? loadingPercentage : 100}
          dataCurrentPage={isQueryDataShown ? currentPage : 1}
          hasRowSelection={false}
          layoutType={TableLayoutType.PART_SALES_HISTORY}
          hasLayoutActions
          hasManageFilters
          initialState={initialState}
          onBodyScroll={onBodyScroll}
        >
          <SlidingPanelContent title={t('titleSalesHistory')} fullWidth={fullWidth}>
            <Box sx={isLoadFailed ? hiddenNoRowsOverlaySx : { display: 'contents' }}>
              <AgGridTable />
            </Box>
          </SlidingPanelContent>
        </AgGridClientTableProvider>
      </SelectionCountProvider>
    </Box>
  );
};
